import React, { useState } from 'react';
import Modal from '@/components/UI/Modal';
import FormSelect from '@/components/UI/FormSelect';
import { Button, DataTable, EmptyState } from '@/components/UI';
import { showToast } from '@/components/Layout/Toast';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { Brokerage, BrokerAccountCapitalSnapshot, BrokerAccountPermissionsSnapshot } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useAppQuery } from '@/hooks/useAppQuery';

interface ThirdPartyAccount {
    brokerConnectionId: number;
    brokerage: Brokerage;
    providerAccountId: string;
    displayAccountId: string | null;
    name: string;
    type: 'paper' | 'live';
    accountType?: string;
    cashBalance?: number | null;
    buyingPower?: number | null;
    optionsBuyingPower?: number | null;
    marketValue?: number | null;
    brokerCapitalSnapshot?: BrokerAccountCapitalSnapshot | null;
    brokerPermissionsSnapshot?: BrokerAccountPermissionsSnapshot | null;
    imported: boolean;
}

interface BrokerConnectionOption {
    id: number;
    brokerage: Brokerage;
    name: string;
    status: string;
    authMethod: 'oauth' | 'apiKeys';
    ownershipMode: 'platformApp' | 'userApp' | 'userKeys';
    environmentScope: 'live' | 'paper' | 'both';
    updatedDateTime: string;
}

interface AddTradingAccountModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (brokerage: string) => void;
    connectedServices: string[];
}

const SUPPORTED_BROKERAGES: Brokerage[] = ['TradeStation', 'Schwab', 'Alpaca'];

function getDefaultBrokerage(connectedServices: string[]): Brokerage {
    return SUPPORTED_BROKERAGES.find((brokerage) => connectedServices.includes(brokerage)) ?? 'TradeStation';
}

function formatCurrency(value?: number | null) {
    if (value === null || value === undefined) return 'N/A';

    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPermissionsSummary(snapshot?: BrokerAccountPermissionsSnapshot | null) {
    if (!snapshot) return 'No broker permissions data';

    const flags = [
        `Margin: ${snapshot.marginMode}`,
        snapshot.optionsApprovalLevel !== null ? `Approval: L${snapshot.optionsApprovalLevel}` : null,
        snapshot.optionsTradingLevel !== null ? `Trading: L${snapshot.optionsTradingLevel}` : null,
        snapshot.maxSupportedStrategyClass ? `Max: ${snapshot.maxSupportedStrategyClass}` : null,
        snapshot.isPatternDayTrader === true ? 'Day-trader flag' : null
    ].filter(Boolean);

    return flags.length ? flags.join(' | ') : 'No broker permissions data';
}

const AddTradingAccountModal: React.FC<AddTradingAccountModalProps> = ({ isOpen, onClose, onAdd, connectedServices }) => {
    const [selectedBrokerage, setSelectedBrokerage] = useState<Brokerage>(getDefaultBrokerage(connectedServices));
    const [selectedBrokerConnectionId, setSelectedBrokerConnectionId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [step, setStep] = useState<'select' | 'accounts'>('select');
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<ThirdPartyAccount | null>(null);
    const [accountName, setAccountName] = useState('');

    const queryClient = useQueryClient();

    const effectiveSelectedBrokerage = connectedServices.includes(selectedBrokerage)
        ? selectedBrokerage
        : getDefaultBrokerage(connectedServices);

    const brokerConnectionsQuery = useAppQuery({
        ...trpc.user.fetchBrokerConnections.queryOptions({ brokerage: effectiveSelectedBrokerage }),
        enabled: isOpen && connectedServices.includes(effectiveSelectedBrokerage)
    });

    const brokerConnections = ((brokerConnectionsQuery.data?.brokerConnections ?? []) as BrokerConnectionOption[]).filter(
        (brokerConnection) => brokerConnection.status === 'active'
    );

    const effectiveSelectedBrokerConnectionId = brokerConnections.some(
        (brokerConnection) => brokerConnection.id === selectedBrokerConnectionId
    )
        ? selectedBrokerConnectionId
        : (brokerConnections[0]?.id ?? null);

    const accountsQuery = useAppQuery({
        ...trpc.user.fetchThirdPartyAccounts.queryOptions({
            brokerage: effectiveSelectedBrokerage,
            brokerConnectionId: effectiveSelectedBrokerConnectionId
        }),
        enabled: false
    });

    const saveTradingAccountMutation = useTrpcMutation(
        trpc.user.saveTradingAccount.mutationOptions({
            onSuccess: (result) => {
                if (!result.tradingAccess.canOpenPositions) showToast(result.tradingAccess.message, 'warning');
                void queryClient.invalidateQueries({ queryKey: trpc.user.listBrokerAccountApprovals.pathKey() });
                void queryClient.invalidateQueries({ queryKey: trpc.admin.listBrokerAccountApprovals.pathKey() });
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchTradingAccounts.queryOptions().queryKey
                });

                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchBrokerConnections.queryOptions().queryKey
                });

                setIsImportModalOpen(false);
                onAdd(selectedAccount?.brokerage || '');
            }
        }),
        {
            successMessage: 'Account successfully imported',
            fallbackErrorMessage: 'Failed to import account'
        }
    );

    const handleBrokerageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedBrokerage(e.target.value as Brokerage);
        setSelectedBrokerConnectionId(null);
        setError(null);
        setStep('select');
    };

    const handleNext = async () => {
        if (!effectiveSelectedBrokerConnectionId) {
            const message = `No ${effectiveSelectedBrokerage} connection is available for account import.`;
            setError(message);
            showToast(message, 'error');
            return;
        }

        setStep('accounts');
        setError(null);

        const result = await accountsQuery.refetch();

        if (result.error) {
            const message =
                result.error instanceof Error ? result.error.message : `Failed to fetch accounts for ${effectiveSelectedBrokerage}`;

            setError(message);
            showToast(message, 'error');
        }
    };

    const handleImportAccount = (account: ThirdPartyAccount) => {
        setSelectedAccount(account);
        setAccountName(account.name);
        setIsImportModalOpen(true);
    };

    const handleConfirmImport = () => {
        if (!selectedAccount) return;

        try {
            saveTradingAccountMutation.mutate({
                brokerage: selectedAccount.brokerage,
                brokerConnectionId: selectedAccount.brokerConnectionId,
                name: accountName,
                accountType: selectedAccount.accountType || 'Margin',
                providerAccountId: selectedAccount.providerAccountId,
                displayAccountId: selectedAccount.displayAccountId,
                type: selectedAccount.type,
                cashBalance: selectedAccount.cashBalance,
                buyingPower: selectedAccount.buyingPower,
                optionsBuyingPower: selectedAccount.optionsBuyingPower,
                marketValue: selectedAccount.marketValue,
                brokerCapitalSnapshot: selectedAccount.brokerCapitalSnapshot ?? null,
                brokerPermissionsSnapshot: selectedAccount.brokerPermissionsSnapshot ?? null
            });
        } catch (mutationError) {
            console.error('Error importing account:', mutationError);
            showToast('Failed to import account', 'error');
        }
    };

    const handleCancelImport = () => {
        setIsImportModalOpen(false);
        setSelectedAccount(null);
        setAccountName('');
    };

    const handleClose = () => {
        setSelectedBrokerage(getDefaultBrokerage(connectedServices));
        setSelectedBrokerConnectionId(null);
        setError(null);
        setStep('select');
        onClose();
    };

    const accounts = accountsQuery.data?.accounts ?? [];

    const selectedBrokerConnection =
        brokerConnections.find((brokerConnection) => brokerConnection.id === effectiveSelectedBrokerConnectionId) ?? null;

    const brokerConnectionOptions = brokerConnections.map((brokerConnection) => ({
        value: brokerConnection.id.toString(),
        label: brokerConnection.status === 'active' ? brokerConnection.name : `${brokerConnection.name} (${brokerConnection.status})`
    }));

    const isLoading = accountsQuery.isFetching;
    const isImporting = saveTradingAccountMutation.isPending;
    const areAllServicesDisabled = SUPPORTED_BROKERAGES.every((brokerage) => !connectedServices.includes(brokerage));

    const columns: ColumnDef<ThirdPartyAccount>[] = [
        {
            accessorKey: 'name',
            header: 'Account Name',
            size: 200
        },
        {
            id: 'displayAccountId',
            header: 'Account ID',
            accessorFn: (account) => account.displayAccountId ?? account.providerAccountId,
            size: 150
        },
        {
            accessorKey: 'type',
            header: 'Type',
            cell: (info) => info.getValue<string>().toUpperCase(),
            size: 100
        },
        {
            accessorKey: 'accountType',
            header: 'Account Type',
            cell: (info) => info.getValue<string>() || 'N/A',
            size: 150
        },
        {
            accessorKey: 'cashBalance',
            header: 'Cash Balance',
            cell: (info) => formatCurrency(info.getValue<number | null | undefined>()),
            size: 150
        },
        {
            accessorKey: 'buyingPower',
            header: 'Buying Power',
            cell: (info) => formatCurrency(info.getValue<number | null | undefined>()),
            size: 150
        },
        {
            accessorKey: 'optionsBuyingPower',
            header: 'Options BP',
            cell: (info) => formatCurrency(info.getValue<number | null | undefined>()),
            size: 150
        },
        {
            accessorKey: 'marketValue',
            header: 'Market Value',
            cell: (info) => formatCurrency(info.getValue<number | null | undefined>()),
            size: 150
        },
        {
            id: 'permissions',
            header: 'Permissions',
            accessorFn: (account) => formatPermissionsSummary(account.brokerPermissionsSnapshot),
            size: 320
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => {
                const account = row.original;
                return account.imported ? (
                    <span className="text-zinc-500">Already Imported</span>
                ) : (
                    <Button size="sm" variant="primary" onClick={() => handleImportAccount(account)}>
                        Import
                    </Button>
                );
            },
            size: 150
        }
    ];

    const modalFooter = (
        <>
            <Button variant="secondary" onClick={handleClose}>
                Cancel
            </Button>
            {step === 'select' ? (
                <Button
                    disabled={
                        !connectedServices.includes(effectiveSelectedBrokerage) ||
                        brokerConnectionsQuery.isFetching ||
                        !effectiveSelectedBrokerConnectionId
                    }
                    variant="primary"
                    onClick={handleNext}
                >
                    Next
                </Button>
            ) : (
                <Button variant="secondary" onClick={() => setStep('select')}>
                    Back
                </Button>
            )}
        </>
    );

    return (
        <Modal
            className="max-w-6xl"
            footer={modalFooter}
            isOpen={isOpen}
            title={step === 'select' ? 'Add Trading Account' : `${effectiveSelectedBrokerage} Accounts`}
            onClose={handleClose}
        >
            {step === 'select' ? (
                <>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="brokerage">
                            Select Brokerage
                        </label>
                        <FormSelect
                            id="brokerage"
                            options={[
                                {
                                    value: 'TradeStation',
                                    label: 'TradeStation',
                                    disabled: !connectedServices.includes('TradeStation')
                                },
                                {
                                    value: 'Schwab',
                                    label: 'Schwab',
                                    disabled: !connectedServices.includes('Schwab')
                                },
                                {
                                    value: 'Alpaca',
                                    label: 'Alpaca',
                                    disabled: !connectedServices.includes('Alpaca')
                                }
                            ]}
                            value={effectiveSelectedBrokerage}
                            onChange={handleBrokerageChange}
                        />
                    </div>

                    {connectedServices.includes(effectiveSelectedBrokerage) && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="brokerConnection">
                                Connection
                            </label>
                            {brokerConnectionsQuery.isFetching ? (
                                <div className="rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-400">
                                    Loading connections...
                                </div>
                            ) : brokerConnectionOptions.length > 1 ? (
                                <FormSelect
                                    id="brokerConnection"
                                    options={brokerConnectionOptions}
                                    value={effectiveSelectedBrokerConnectionId?.toString() ?? ''}
                                    onChange={(e) => setSelectedBrokerConnectionId(Number(e.target.value))}
                                />
                            ) : selectedBrokerConnection ? (
                                <div className="rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-200">
                                    {selectedBrokerConnection.name}
                                </div>
                            ) : (
                                <div className="rounded-md border border-yellow-700/50 bg-yellow-900/20 px-3 py-2 text-sm text-yellow-200">
                                    No active connection found for {effectiveSelectedBrokerage}.
                                </div>
                            )}
                        </div>
                    )}

                    {areAllServicesDisabled && (
                        <div className="mt-4 rounded-md border border-yellow-700/50 bg-yellow-900/30 p-3 text-sm text-yellow-200">
                            <p>
                                You need to connect to a brokerage service before adding trading accounts. Please visit the{' '}
                                <strong>Third-Party Connections</strong> page to connect your brokerage accounts.
                            </p>
                        </div>
                    )}
                </>
            ) : (
                <>
                    {error && (
                        <div className="mb-4 rounded-md border border-red-700/50 bg-red-900/30 p-3 text-sm text-red-200">{error}</div>
                    )}

                    {isLoading ? (
                        <div className="py-8 text-center">
                            <div className="inline-block h-8 w-8 animate-spin rounded-full border-t-2 border-b-2 border-blue-500" />
                            <p className="mt-2 text-zinc-300">Loading accounts...</p>
                        </div>
                    ) : accounts.length === 0 ? (
                        <EmptyState title={<>No accounts found for {effectiveSelectedBrokerage}.</>} />
                    ) : (
                        <div className="mb-4">
                            <p className="mb-3 text-sm text-zinc-300">Select an account to import:</p>
                            <div className="overflow-x-auto">
                                <DataTable allowOverflow striped columns={columns} data={accounts} />
                            </div>
                        </div>
                    )}
                </>
            )}

            {isImportModalOpen && selectedAccount && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                    <div className="w-full max-w-md rounded-md bg-[#212529] p-4 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-lg font-medium text-white">Import Account</h3>
                            <button className="text-zinc-400 hover:text-white" type="button" onClick={handleCancelImport}>
                                &times;
                            </button>
                        </div>

                        <div className="mb-4">
                            <label className="mb-1 block text-sm font-medium text-zinc-200" htmlFor="accountName">
                                Account Name
                            </label>
                            <input
                                className="w-full rounded-md border border-zinc-700 bg-zinc-800 p-2 text-white"
                                disabled={isImporting}
                                id="accountName"
                                type="text"
                                value={accountName}
                                onChange={(e) => setAccountName(e.target.value)}
                            />
                        </div>

                        <div className="flex justify-end space-x-2">
                            <Button disabled={isImporting} variant="secondary" onClick={handleCancelImport}>
                                Cancel
                            </Button>
                            <Button disabled={isImporting || !accountName.trim()} variant="primary" onClick={handleConfirmImport}>
                                {isImporting ? 'Importing...' : 'Import'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default AddTradingAccountModal;
